import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type FilterFn,
  type SortingState,
  type Table,
  type VisibilityState,
} from "@tanstack/react-table";
import { useState } from "react";
import { usePersistedColumnVisibility } from "./use-persisted-column-visibility";

type UseClientDataTableOptions<TData> = {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  /** localStorage key for column visibility, e.g. c456.datatable.members.columns */
  storageKey: string;
  defaultColumnVisibility?: VisibilityState;
  /** 始终显示、不可被列设置关掉的列 id */
  forcedVisibleColumnIds?: string[];
  defaultPageSize?: number;
  globalFilterFn?: FilterFn<TData>;
};

export function useClientDataTable<TData>({
  data,
  columns,
  storageKey,
  defaultColumnVisibility = {},
  forcedVisibleColumnIds = [],
  defaultPageSize = 10,
  globalFilterFn,
}: UseClientDataTableOptions<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = usePersistedColumnVisibility(
    storageKey,
    defaultColumnVisibility,
    forcedVisibleColumnIds,
  );
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((current) => {
        const next = typeof updater === "function" ? updater(current) : updater;
        const locked = { ...next };
        for (const id of forcedVisibleColumnIds) {
          locked[id] = true;
        }
        return locked;
      });
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn,
  });

  return {
    table,
    globalFilter,
    setGlobalFilter,
  };
}

export type { Table, ColumnDef, VisibilityState };
export { flexRender } from "@tanstack/react-table";
